/**
 * Apply-by-email helpers. Some postings (HN "Who is Hiring", small companies)
 * ask candidates to email the employer directly rather than fill a form. For
 * those jobs (`jobs.apply_email` set) the apply CTA and the extension open a
 * prefilled Gmail compose draft so the user can apply in one click instead of
 * hand-copying the address. See `src/lib/ingestion/hn.ts → extractApplyEmail`.
 */

export type JobApplyEmailDraft = {
	to: string;
	subject: string;
	body: string;
	/**
	 * Optional BCC to `apply+<trackerId>@apply.lokerdollar.com` — the send
	 * proof. A copy of the real message lands on infrastructure we own, which
	 * is the only way this platform can observe an application leaving instead
	 * of being told one did. See src/lib/apply/send-proof.ts.
	 *
	 * Every channel below must carry it or the proof is silently channel-
	 * dependent: a seeker who picks Outlook would be uncounted while a Gmail
	 * user is counted, and the resulting numbers would describe mail clients.
	 */
	bcc?: string;
	/**
	 * Optional VISIBLE Cc — the same relay address, when the
	 * `apply-handoff-cc-relay` flag is on (owner decision 2026-10-29). On the
	 * Bcc line the employer never sees the relay, so a reply-all never reaches
	 * us and `application_replies` stayed at 0 rows. On the Cc line a reply-all
	 * lands on `apply+<trackerId>@` and is recorded. Set this OR `bcc`, never
	 * both (see {@link withApplyRelay}). Every channel builder emits it.
	 */
	cc?: string;
};

/**
 * PostHog flag — the SOLE switch for carrying the relay on the visible Cc
 * line instead of Bcc. ENABLED ⇒ Cc; absent/off ⇒ today's Bcc. Read
 * server-side per user (`getServerFeatureFlagForUser`).
 */
export const APPLY_HANDOFF_CC_RELAY_FLAG = "apply-handoff-cc-relay";

/**
 * Attach the `apply+<trackerId>@` relay to a draft: on the visible Cc line
 * when `cc` is true, on the Bcc line otherwise. Never both — a message that
 * carries the address twice is delivered once anyway, and Cc+Bcc would make
 * the draft look like a mistake to the seeker.
 */
export function withApplyRelay<T extends JobApplyEmailDraft>(
	draft: T,
	relay: string,
	opts: { cc: boolean },
): T {
	const { bcc: _bcc, cc: _cc, ...rest } = draft;
	return (opts.cc ? { ...rest, cc: relay } : { ...rest, bcc: relay }) as T;
}

/**
 * Build a Gmail "compose new message" URL with prefilled recipient/subject/body.
 * `view=cm&fs=1` opens the full-screen compose window. Works for any logged-in
 * Gmail user; a non-Gmail user can still copy the address from the draft.
 */
export function buildGmailComposeUrl(draft: JobApplyEmailDraft): string {
	const params = new URLSearchParams({
		view: "cm",
		fs: "1",
		to: draft.to,
		su: draft.subject,
		body: draft.body,
		...(draft.cc ? { cc: draft.cc } : {}),
		...(draft.bcc ? { bcc: draft.bcc } : {}),
	});
	return `https://mail.google.com/mail/?${params.toString()}`;
}

/**
 * The subject line every apply-by-email draft carries:
 * `Application: <title> at <company>` (en) / `Lamaran: <title> di <company>` (id).
 *
 * Exported because it is also an IDENTITY: the seeker's Bcc copy of their own
 * application arrives on `apply+<trackerId>@` with this exact subject, and
 * send-proof uses it to recognise that copy when the seeker sent from a
 * different mailbox than their account email (src/lib/apply/send-proof.ts).
 */
export function buildApplyEmailSubject(opts: {
	jobTitle: string;
	company?: string | null;
	locale?: string;
}): string {
	const isEn = opts.locale !== "id";
	const at = opts.company
		? isEn
			? ` at ${opts.company}`
			: ` di ${opts.company}`
		: "";
	return isEn
		? `Application: ${opts.jobTitle}${at}`
		: `Lamaran: ${opts.jobTitle}${at}`;
}

/**
 * Compose a localized application-email skeleton (subject + body) for a job.
 *
 * Two modes:
 *
 *  - **Draft mode** (no `coverLetter`) — the original behavior used by the
 *    one-click Gmail compose URL: a clear subject and an editable opener that
 *    names the role/company, leaving the candidate to add their own pitch. The
 *    posting often asks for extras (GitHub, a writeup); we hint that without
 *    inventing content.
 *  - **Auto-send mode** (`coverLetter` set) — used by the auto email-apply engine
 *    (`runApplyEmailSendJob`). The AI-generated cover letter IS the body, so the
 *    email reads as a finished application, not a half-filled template. When the
 *    seeker has no uploaded resume FILE to attach, pass `resumeText` to inline
 *    the plain-text resume under a clear label so the employer still gets it.
 */
export function buildJobApplyEmailDraft(opts: {
	applyEmail: string;
	jobTitle: string;
	company?: string | null;
	locale?: string;
	/** AI cover letter — when present it replaces the editable placeholder body. */
	coverLetter?: string;
	/** Plain-text resume to inline (used only when no resume FILE is attached). */
	resumeText?: string;
}): JobApplyEmailDraft {
	const isEn = opts.locale !== "id";
	const at = opts.company
		? isEn
			? ` at ${opts.company}`
			: ` di ${opts.company}`
		: "";
	const subject = buildApplyEmailSubject(opts);

	const cover = opts.coverLetter?.trim();
	let body = cover
		? `${cover}\n`
		: isEn
			? `Hi,\n\nI'd like to apply for the ${opts.jobTitle} role${at}.\n\n[Add a sentence or two about yourself and why you're a fit. Attach your CV/resume and anything the posting asked for — e.g. GitHub or a short writeup.]\n\nThanks,\n`
			: `Halo,\n\nSaya ingin melamar posisi ${opts.jobTitle}${at}.\n\n[Tambahkan satu-dua kalimat tentang dirimu dan kenapa cocok. Lampirkan CV/resume dan apa pun yang diminta di lowongan — misalnya GitHub atau tulisan singkat.]\n\nTerima kasih,\n`;

	const resume = opts.resumeText?.trim();
	if (resume) {
		const label = isEn
			? "\n\n--- Resume / CV ---\n"
			: "\n\n--- Resume / CV ---\n";
		body += `${label}${resume}\n`;
	}

	return { to: opts.applyEmail, subject, body };
}

/** Convenience: full Gmail compose URL for a job's apply-by-email address. */
export function buildJobApplyGmailUrl(opts: {
	applyEmail: string;
	jobTitle: string;
	company?: string | null;
	locale?: string;
}): string {
	return buildGmailComposeUrl(buildJobApplyEmailDraft(opts));
}
